import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  Button,
  Modal,
  Form,
  InputGroup,
  Table,
  Badge,
  Spinner,
  Pagination,
  ProgressBar,
  Dropdown,
  DropdownButton
} from 'react-bootstrap';
import { 
  FaSearch,
  FaFilter,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileImage,
  FaDownload,
  FaEdit,
  FaTrash,
  FaUpload,
  FaPlus,
  FaEye,
  FaCalendarAlt,
  FaGraduationCap,
  FaBook,
  FaFileAlt,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaTimes
} from 'react-icons/fa';
import axios from 'axios';
import AdminNavbar from './components/AdminNavbar';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ProspectusManagement = () => {
  const [prospectus, setProspectus] = useState([]);
  const [filteredProspectus, setFilteredProspectus] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedProgram, setSelectedProgram] = useState('all');
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProspectus, setSelectedProspectus] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    academic_year_id: '',
    program_name: '',
    title: '',
    description: '',
    file_path: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('uploaded_at');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // API URLs
  const API_URL = `${BASE_URL}/prospectus.php`;
  const UPLOAD_URL = `${BASE_URL}/upload_prospectus.php`;
  
  // Initialize user
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    if (!userData) {
      // Default admin user
      setUser({
        name: 'Administrator',
        email: 'admin@college.edu',
        role: 'Super Admin',
        avatar: null
      });
    } else {
      setUser(userData);
    }
  }, []);
  
  // Helper functions
  const getFileIcon = (filePath) => {
    if (!filePath) return <FaFileAlt className="text-secondary" />;
    const extension = filePath.split('.').pop().toLowerCase();
    
    const FILE_ICONS = {
      pdf: <FaFilePdf className="text-danger" />,
      doc: <FaFileWord className="text-primary" />,
      docx: <FaFileWord className="text-primary" />,
      jpg: <FaFileImage className="text-success" />,
      jpeg: <FaFileImage className="text-success" />,
      png: <FaFileImage className="text-success" />,
      xls: <FaFileExcel className="text-success" />,
      xlsx: <FaFileExcel className="text-success" />
    };
    
    return FILE_ICONS[extension] || <FaFileAlt className="text-secondary" />;
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };
  
  // Get full file URL
  const getFileUrl = (filePath) => {
    if (!filePath) return null;
    // Remove leading slash if present
    const cleanPath = filePath.startsWith('/') ? filePath.substring(1) : filePath;
    return `${UPLOAD_BASE_URL}/${cleanPath}`;
  };
  
  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    window.location.href = '/admin/login';
  };
  
  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch prospectus
      const response = await axios.get(API_URL, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      console.log('API Response:', response.data);
      
      if (response.data.success && Array.isArray(response.data.data)) {
        const prospectusData = response.data.data.map(item => ({
          ...item,
          id: parseInt(item.id),
          academic_year_id: parseInt(item.academic_year_id),
          formattedDate: formatDate(item.uploaded_at),
          file_extension: item.file_path ? item.file_path.split('.').pop().toLowerCase() : null,
          file_url: getFileUrl(item.file_path)
        }));
        
        setProspectus(prospectusData);
        setFilteredProspectus(prospectusData);
      } else {
        setError(response.data.message || 'No prospectus data available');
        setProspectus([]);
        setFilteredProspectus([]);
      }
      
      // Fetch academic years
      const yearsResponse = await axios.get(`${API_URL}?academic_years=1`);
      if (yearsResponse.data.success) {
        setAcademicYears(yearsResponse.data.data);
      }
      
      // Fetch programs
      const programsResponse = await axios.get(`${API_URL}?programs=1`);
      if (programsResponse.data.success) {
        setPrograms(programsResponse.data.data);
      }
      
    } catch (err) {
      console.error('Error fetching data:', err.response || err);
      setError('Failed to load prospectus data. Please try again later.');
      setProspectus([]);
      setFilteredProspectus([]);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Filter and sort prospectus
  useEffect(() => {
    let filtered = [...prospectus];
    
    // Filter by academic year
    if (selectedYear !== 'all') {
      filtered = filtered.filter(item => item.academic_year_id == selectedYear);
    }
    
    // Filter by program
    if (selectedProgram !== 'all') {
      filtered = filtered.filter(item => item.program_name === selectedProgram);
    }
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.title.toLowerCase().includes(term) ||
        item.program_name.toLowerCase().includes(term) ||
        (item.description && item.description.toLowerCase().includes(term))
      );
    }
    
    // Sort data
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      if (sortField === 'uploaded_at' || sortField === 'updated_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
    
    setFilteredProspectus(filtered);
    setCurrentPage(1);
  }, [prospectus, selectedYear, selectedProgram, searchTerm, sortField, sortDirection]);
  
  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredProspectus.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredProspectus.length / itemsPerPage);
  
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File size must be less than 50MB');
      return;
    }
    
    const allowedTypes = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png', 'xls', 'xlsx'];
    const fileExtension = file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(fileExtension)) {
      toast.error('Invalid file type. Allowed: PDF, Word, Excel, JPEG, PNG');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      // Get token from localStorage or use default
      const token = localStorage.getItem('token') || 'test-token-123';
      
      const response = await axios.post(UPLOAD_URL, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      });
      
      console.log('Upload response:', response.data);
      
      if (response.data.success) {
        setFormData(prev => ({
          ...prev,
          file_path: response.data.uploaded_file.file_path
        }));
        toast.success('File uploaded successfully');
      } else {
        toast.error(response.data.message || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err.response || err);
      toast.error(err.response?.data?.message || 'Failed to upload file');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('Form data:', formData);
    
    if (!formData.file_path) {
      toast.error('Please upload a file first');
      return;
    }
    
    if (!formData.academic_year_id || !formData.program_name || !formData.title) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      let response;
      
      // Prepare data for API
      const apiData = {
        academic_year_id: parseInt(formData.academic_year_id),
        program_name: formData.program_name,
        title: formData.title,
        description: formData.description || '',
        file_path: formData.file_path,
        uploaded_by: 1 // Default admin ID
      };
      
      console.log('Sending data:', apiData);
      
      if (isEditing) {
        response = await axios.put(`${API_URL}?id=${selectedProspectus.id}`, apiData);
      } else {
        response = await axios.post(API_URL, apiData);
      }
      
      console.log('API response:', response.data);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setShowModal(false);
        resetForm();
        fetchData();
      } else {
        toast.error(response.data.message || 'Operation failed');
      }
    } catch (err) {
      console.error('Submit error:', err.response || err);
      const errorMsg = err.response?.data?.message || 'Operation failed. Please try again.';
      toast.error(errorMsg);
    }
  };
  
  const handleEdit = (item) => {
    console.log('Editing item:', item);
    setSelectedProspectus(item);
    setFormData({
      academic_year_id: item.academic_year_id.toString(),
      program_name: item.program_name,
      title: item.title,
      description: item.description || '',
      file_path: item.file_path
    });
    setIsEditing(true);
    setShowModal(true);
  };
  
  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      const response = await axios.delete(`${API_URL}?id=${deleteId}`);
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchData();
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to delete prospectus');
    } finally {
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };
  
  const resetForm = () => {
    setFormData({
      academic_year_id: '',
      program_name: '',
      title: '',
      description: '',
      file_path: ''
    });
    setSelectedProspectus(null);
    setIsEditing(false);
  };
  
  const handleDownload = (fileUrl) => {
    if (fileUrl) {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileUrl.split('/').pop();
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };
  
  const handlePreview = (fileUrl) => {
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  };
  
  // Statistics
  const getStatistics = () => {
    const total = prospectus.length;
    const uniquePrograms = new Set(prospectus.map(p => p.program_name)).size;
    const uniqueYears = new Set(prospectus.map(p => p.academic_year_id)).size;
    
    return { total, uniquePrograms, uniqueYears };
  };
  
  const stats = getStatistics();
  
  if (loading) {
    return (
      <>
        <AdminNavbar onLogout={handleLogout} user={user} />
        <Container className="py-5">
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
            <h4 className="mt-4" style={{ color: '#1a237e' }}>Loading Prospectus Data</h4>
            <p className="text-muted">Please wait while we load the prospectus information...</p>
          </div>
        </Container>
      </>
    );
  }
  
  return (
    <>
      <AdminNavbar onLogout={handleLogout} user={user} />
      <div className="py-4" style={{ background: '#f8f9fa', minHeight: '100vh' }}>
        <ToastContainer position="top-right" autoClose={3000} />
        
        <Container>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1 className="h3 mb-2" style={{ color: '#1a237e' }}>
                <FaBook className="me-2" />
                Prospectus Management
              </h1>
              <p className="text-muted mb-0">
                Manage academic prospectus for different programs and years
              </p>
            </div>
            <Button 
              variant="primary" 
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
            >
              <FaPlus className="me-2" />
              Add Prospectus
            </Button>
          </div>
          
          {/* Statistics Cards */}
          <Row className="mb-4">
            <Col md={4}>
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <div style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '24px'
                      }}>
                        <FaFileAlt />
                      </div>
                    </div>
                    <div>
                      <h3 className="mb-0">{stats.total}</h3>
                      <small className="text-muted">Total Prospectus</small>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <div style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '24px'
                      }}>
                        <FaGraduationCap />
                      </div>
                    </div>
                    <div>
                      <h3 className="mb-0">{stats.uniquePrograms}</h3>
                      <small className="text-muted">Programs</small>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <div style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '24px'
                      }}>
                        <FaCalendarAlt />
                      </div>
                    </div>
                    <div>
                      <h3 className="mb-0">{stats.uniqueYears}</h3>
                      <small className="text-muted">Academic Years</small>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          
          {/* Search and Filter */}
          <Card className="mb-4 border-0 shadow-sm">
            <Card.Body>
              <Row className="g-3">
                <Col md={6}>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaSearch />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search by title, program, or description..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <Button 
                        variant="outline-secondary" 
                        onClick={() => setSearchTerm('')}
                      >
                        <FaTimes />
                      </Button>
                    )}
                  </InputGroup>
                </Col>
                <Col md={3}>
                  <Form.Select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                  >
                    <option value="all">All Academic Years</option>
                    {academicYears.map(year => (
                      <option key={year.id} value={year.id}>
                        {year.academic_year}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Select
                    value={selectedProgram}
                    onChange={(e) => setSelectedProgram(e.target.value)}
                  >
                    <option value="all">All Programs</option>
                    {programs.map((program, index) => (
                      <option key={index} value={program.program_name}>
                        {program.program_name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          
          {/* Prospectus Table */}
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleSort('title')}>
                        Title {sortField === 'title' && (
                          sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />
                        )}
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleSort('program_name')}>
                        Program {sortField === 'program_name' && (
                          sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />
                        )}
                      </th>
                      <th>Academic Year</th>
                      <th>File Type</th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleSort('uploaded_at')}>
                        Uploaded {sortField === 'uploaded_at' && (
                          sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />
                        )}
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.length > 0 ? (
                      currentItems.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <div className="d-flex align-items-center">
                              {getFileIcon(item.file_path)}
                              <div className="ms-3">
                                <strong>{item.title}</strong>
                                {item.description && (
                                  <div className="text-muted small" style={{ maxWidth: '300px' }}>
                                    {item.description.length > 50 
                                      ? `${item.description.substring(0, 50)}...` 
                                      : item.description}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>
                            <Badge bg="info" className="px-3 py-2">
                              {item.program_name}
                            </Badge>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <FaCalendarAlt className="me-2 text-muted" />
                              <span>{item.academic_year}</span>
                            </div>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              {getFileIcon(item.file_path)}
                              <span className="ms-2 text-capitalize">
                                {item.file_extension || 'N/A'}
                              </span>
                            </div>
                          </td>
                          <td>
                            <small className="text-muted">
                              {item.formattedDate}
                            </small>
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handlePreview(item.file_url)}
                                disabled={!item.file_url}
                                title="Preview"
                              >
                                <FaEye />
                              </Button>
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => handleDownload(item.file_url)}
                                disabled={!item.file_url}
                                title="Download"
                              >
                                <FaDownload />
                              </Button>
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => handleEdit(item)}
                                title="Edit"
                              >
                                <FaEdit />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => {
                                  setDeleteId(item.id);
                                  setShowDeleteModal(true);
                                }}
                                title="Delete"
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center py-5">
                          <FaFileAlt size={48} className="text-muted mb-3" />
                          <h5 className="mb-3">No Prospectus Found</h5>
                          <p className="text-muted mb-4">
                            {searchTerm || selectedYear !== 'all' || selectedProgram !== 'all'
                              ? 'No results match your filters'
                              : 'No prospectus records available'}
                          </p>
                          <Button 
                            variant="primary"
                            onClick={() => {
                              setSearchTerm('');
                              setSelectedYear('all');
                              setSelectedProgram('all');
                            }}
                          >
                            Clear Filters
                          </Button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
            
            {/* Pagination */}
            {filteredProspectus.length > 0 && (
              <Card.Footer className="bg-white border-0">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="text-muted">
                    Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredProspectus.length)} of {filteredProspectus.length} entries
                  </div>
                  <Pagination className="mb-0">
                    <Pagination.Prev 
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                    />
                    {[...Array(totalPages)].map((_, i) => (
                      <Pagination.Item
                        key={i + 1}
                        active={i + 1 === currentPage}
                        onClick={() => setCurrentPage(i + 1)}
                      >
                        {i + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next 
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              </Card.Footer>
            )}
          </Card>
        </Container>
        
        {/* Add/Edit Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {isEditing ? 'Edit Prospectus' : 'Add New Prospectus'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Academic Year <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      name="academic_year_id"
                      value={formData.academic_year_id}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Academic Year</option>
                      {academicYears.map(year => (
                        <option key={year.id} value={year.id}>
                          {year.academic_year}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Program Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="program_name"
                      value={formData.program_name}
                      onChange={handleInputChange}
                      placeholder="e.g., Computer Science, Business Administration"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Title <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="e.g., Undergraduate Program Prospectus 2024"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Description of the prospectus..."
                      rows={3}
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>File <span className="text-danger">*</span></Form.Label>
                    {formData.file_path ? (
                      <div className="border p-3 rounded bg-light mb-3">
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            {getFileIcon(formData.file_path)}
                            <div className="ms-3">
                              <strong>{formData.file_path.split('/').pop()}</strong>
                              <div className="text-success small">
                                File uploaded successfully
                              </div>
                            </div>
                          </div>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => setFormData(prev => ({ ...prev, file_path: '' }))}
                          >
                            <FaTimes />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="border border-dashed p-5 text-center rounded">
                        <FaUpload size={48} className="text-muted mb-3" />
                        <h5>Upload Prospectus File</h5>
                        <p className="text-muted mb-3">
                          Supported formats: PDF, Word, Excel, JPEG, PNG (Max 50MB)
                        </p>
                        <Form.Control
                          type="file"
                          onChange={handleFileUpload}
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx"
                          disabled={uploading}
                        />
                        {uploading && (
                          <div className="mt-3">
                            <ProgressBar 
                              now={uploadProgress} 
                              label={`${uploadProgress}%`} 
                              animated 
                            />
                            <small className="text-muted d-block mt-2">
                              Uploading... {uploadProgress}%
                            </small>
                          </div>
                        )}
                      </div>
                    )}
                    <Form.Text className="text-muted">
                      Upload the prospectus file. Max size: 50MB
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                disabled={!formData.file_path || uploading}
              >
                {isEditing ? 'Update Prospectus' : 'Create Prospectus'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
        
        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="warning" className="border-0">
              <div className="d-flex align-items-center">
                <FaTrash className="me-3" size={24} />
                <div>
                  <h5 className="alert-heading">Are you sure?</h5>
                  <p className="mb-0">
                    This will permanently delete the prospectus record and associated file.
                    This action cannot be undone.
                  </p>
                </div>
              </div>
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Delete Prospectus
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default ProspectusManagement;